import {
    CellFormatterService,
    TableOptions
} from '../common/datatable';
import { TranslationService } from '../services/translation.service';
import { FeatureFlagService } from '../services/feature-flags.service';
import { JobOrder, Order } from '@common/types';
import { addAuditColumnsToOptions, ignoreColumnsInOptions, pipe } from '@common/datatable/utils';

export class OrderTableOptions {
    options: TableOptions;
    isGLP = false;

    constructor(
        private cellFormatterService: CellFormatterService,
        private translationService: TranslationService,
        private featureFlagService: FeatureFlagService,
    ) {
        this.initIsGLP();
        this.options = pipe(
            this.getDefaultOptions(),
            addAuditColumnsToOptions(cellFormatterService),
            ignoreColumnsInOptions(),
        );
    }

    getDefaultOptions(): TableOptions {
        return {
            csvFileName: 'order.csv',
            enableDetailColumn: true,
            enableDraggable: false,
            enableSelectable: true,
            columnsForcedToExport: [
                {
                    displayName: 'Order Type',
                    field: 'cv_MaterialType.MaterialType',
                    forcedToExport: this.isGLP,
                    position: 2
                }
            ],
            columns: [
                {
                    displayName: 'ID',
                    field: 'OrderID',
                    sortField: 'OrderSortableFields.OrderIdSortable'
                },
                {
                    displayName: 'Type',
                    field: 'cv_MaterialType.MaterialType',
                    ignore: this.isGLP
                },
                {
                    displayName: 'Status',
                    field: 'cv_OrderStatus.OrderStatus',
                    visible: false
                },
                {
                    displayName: 'Animal/Sample Origin',
                    field: 'cv_MaterialOrigin.MaterialOrigin'
                },
                {
                    displayName: `${this.translationService.translate('Institutions')}`,
                    field: 'OrderSortableFields.InstitutionNamesSortable',
                    visible: false
                },
                {
                    displayName: 'Sites',
                    field: 'OrderSortableFields.SiteNamesSortable',
                    visible: false
                },
                {
                    displayName: 'Countries of Origin',
                    field: 'OrderSortableFields.CountriesOfOriginSortable',
                    visible: false
                },
                {
                    displayName: this.translationService.translate('Job') + ' Name',
                    field: 'Job.JobID',
                    sortField: 'Job.JobIDSortable'
                },
                {
                    displayName: `Requested ${this.translationService.translate('Line')}`,
                    field: 'Line.LineName',
                    visible: false,
                },
                {
                    displayName: 'Number of Animals Ordered',
                    field: 'AnimalCount',
                    visible: false
                },
                {
                    displayName: 'Requested Sex',
                    field: 'cv_Sex.Sex',
                    visible: false
                },
                {
                    displayName: 'Requested Age Range',
                    field: 'RequestedAgeRange',
                    sortField: 'OrderSortableFields.RequestedAgeRangeSortable',
                    visible: false
                },
                {
                    displayName: 'Requested Weight Range',
                    field: 'RequestedWeightRange',
                    sortField: 'OrderSortableFields.RequestedWeightRangeSortable',
                    visible: false
                },
                {
                    displayName: 'Quarantine Services Facility',
                    field: 'cv_QuarantineFacility.QuarantineFacility',
                    visible: false
                },
                {
                    displayName: 'Locations',
                    field: 'CurrentLocationPath'
                },
                {
                    displayName: 'PO Number',
                    field: 'InvoiceNumber'
                },
                {
                    displayName: 'Vendor Order Number',
                    field: 'VendorOrderNumber'
                },
                {
                    displayName: 'Vendor Room ID',
                    field: 'VendorRoomId',
                    visible: false
                },
                {
                    displayName: 'Date Ordered',
                    field: 'DateReceived',
                    visible: false,
                    formatter: this.cellFormatterService.dateFormatter
                },
                {
                    displayName: 'Projected Receipt Date',
                    field: 'DateProjectedReceipt',
                    visible: false,
                    formatter: this.cellFormatterService.dateFormatter
                },
                {
                    displayName: 'CITES',
                    field: 'Cites',
                    visible: false
                },
                {
                    displayName: 'Numbers of Animals Received',
                    field: 'OrderSortableFields.NumbersOfAnimalsReceived',
                    sortField: 'OrderSortableFields.NumbersOfAnimalsReceivedSortable',
                    visible: false
                },
                {
                    displayName: 'Sexes Received',
                    field: 'OrderSortableFields.SexesReceivedSortable',
                    visible: false,
                },
                {
                    displayName: `${this.translationService.translate('Lines')} Received`,
                    field: 'OrderSortableFields.LinesReceivedSortable',
                    visible: false,
                },
                {
                    displayName: 'Species Received',
                    field: 'OrderSortableFields.SpeciesReceivedSortable'
                },
                {
                    displayName: 'Animal Conditions at Receipt',
                    field: 'OrderSortableFields.AnimalConditionsAtReceiptSortable',
                    visible: false,
                },
                {
                    displayName: 'Shipment Container Conditions at Receipt',
                    field: 'OrderSortableFields.ShipmentContainerConditionsAtReceiptSortable',
                    visible: false,
                },
                {
                    displayName: `Receipt Dates`,
                    field: 'OrderSortableFields.ReceiptDatesSortable',
                    visible: false,
                },
                {
                    displayName: 'Sample Condition',
                    field: 'cv_SampleCondition.SampleCondition',
                },
                {
                    displayName: 'Locked', field: 'IsLocked',
                    formatter: this.cellFormatterService.lockedFormatter,
                    exportFormatter: (_: Order, value: boolean | null) => {
                        return value ? 'Yes' : 'No';
                    },
                    maxWidth: 80,
                    visible: false
                },
            ]
        };
    }

    initIsGLP(): void {
        this.isGLP = this.featureFlagService.getIsGLP();
    }
}
